import React, { FC, ReactNode, createContext, useContext, useState, useCallback, useMemo } from "react";
import { type PermissionsSyncPayload } from "@bloomstack/apis/services/IEntityPermissions";
import { WILDCARD_ALL } from "@bloomstack/apis/entityPermissions";

/** Default empty permissions state */
export const EMPTY_PERMISSIONS: PermissionsSyncPayload["perms"] = {
  app: [],
  user: [],
  orgs: {},
  workspaces: {},
};

/** User details from backend session */
export type AppSessionUser = {
  /** User's ID (from User table) */
  userId: string | undefined;
  /** User's profile ID (from UserProfile table) */
  profileId: string | undefined;
  /** User's display name (from UserProfile.name — the username shown in chat) */
  name: string | undefined;
  /** User's avatar image URL */
  image: string | undefined;
  /** UI capabilities enabled for this user */
  uiCapabilities: string[];
};

/** Default empty user state */
export const EMPTY_USER: AppSessionUser = {
  userId: undefined,
  profileId: undefined,
  name: undefined,
  image: undefined,
  uiCapabilities: [],
};

export type AppSessionContextApi = {
  /** Whether session has been initialized from backend */
  isInitialized: boolean;
  /** User details from backend */
  user: AppSessionUser;
  /** Current permissions from server */
  permissions: PermissionsSyncPayload["perms"];
  /** Update session from backend payload */
  setSession: (payload: PermissionsSyncPayload) => void;
  /** Clear session (called on disconnect) */
  clearSession: () => void;
  /**
   * Check if user has permission at app level (default) or specific entity level.
   * Supports wildcard (*) for super admins.
   * @param permission - Permission key to check
   * @param workspaceId - Optional workspace ID to check workspace-level permission
   * @param orgId - Optional org ID to check org-level permission
   */
  hasPermission: (permission: string, workspaceId?: string, orgId?: string) => boolean;
};

export const AppSessionContext = createContext<AppSessionContextApi | null>(null);

/**
 * Hook to access the app session context.
 * Returns null-safe fallback if used outside provider.
 */
export const useAppSession = (): AppSessionContextApi => {
  const context = useContext(AppSessionContext);
  if (!context) {
    // Return a safe fallback - session won't work but won't crash
    return {
      isInitialized: false,
      user: EMPTY_USER,
      permissions: EMPTY_PERMISSIONS,
      setSession: () => {},
      clearSession: () => {},
      hasPermission: () => false,
    };
  }
  return context;
};

export type AppSessionProviderProps = {
  children: ReactNode;
};

/**
 * App session provider.
 * 
 * This provider should wrap everything, including SocketIOProvider.
 * It holds the user session state (user details + permissions) and provides
 * the hasPermission method that all other hooks can use.
 * 
 * SocketIOProvider calls setSession when permissions are synced from server.
 * useUser reads user details from this context.
 */
export const AppSessionProvider: FC<AppSessionProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AppSessionUser>(EMPTY_USER);
  const [permissions, setPermissions] = useState<PermissionsSyncPayload["perms"]>(EMPTY_PERMISSIONS);
  const [isInitialized, setIsInitialized] = useState(false);

  const setSession = useCallback((payload: PermissionsSyncPayload) => {
    setUser({
      userId: payload.userId,
      profileId: payload.profileId,
      name: payload.name,
      image: payload.image,
      uiCapabilities: payload.uiCapabilities || [],
    });
    setPermissions(payload.perms);
    setIsInitialized(true);
  }, []);

  const clearSession = useCallback(() => {
    console.log('[AppSession] Session cleared');
    setUser(EMPTY_USER);
    setPermissions(EMPTY_PERMISSIONS);
    // Note: We don't reset isInitialized here - once initialized, stay initialized
    // This prevents flashing the loading screen on reconnects
  }, []);

  const hasPermission = useCallback((permission: string, workspaceId?: string, orgId?: string): boolean => {
    // 1. Check wildcard at app level (super admin)
    if (permissions.app.includes(WILDCARD_ALL)) {
      return true;
    }

    // 2. Check app-level permissions
    if (permissions.app.includes(permission)) {
      return true;
    }

    // 2.5. Check user-level permissions (baseline capabilities)
    if (permissions.user?.includes(permission)) {
      return true;
    }

    // 3. If orgId provided, check org-level permissions (including wildcard)
    if (orgId) {
      const orgPerms = permissions.orgs[orgId];
      if (orgPerms?.includes(WILDCARD_ALL) || orgPerms?.includes(permission)) {
        return true;
      }
    }

    // 4. If workspaceId provided, check workspace-level permissions (including wildcard)
    if (workspaceId) {
      const wsPerms = permissions.workspaces[workspaceId];
      if (wsPerms?.includes(WILDCARD_ALL) || wsPerms?.includes(permission)) {
        return true;
      }
    }

    return false;
  }, [permissions]);

  const api = useMemo<AppSessionContextApi>(() => ({
    isInitialized,
    user,
    permissions,
    setSession,
    clearSession,
    hasPermission,
  }), [isInitialized, user, permissions, setSession, clearSession, hasPermission]);

  return (
    <AppSessionContext.Provider value={api}>
      {children}
    </AppSessionContext.Provider>
  );
};

// Re-export for backward compatibility during migration
export { 
  AppSessionProvider as PermissionsStateProvider,
  useAppSession as usePermissionsState,
  AppSessionContext as PermissionsStateContext,
};
export type { AppSessionContextApi as PermissionsStateContextApi };
